import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { DateStep } from "../booking/DateStep";
import { TimeStep } from "../booking/TimeStep";

const InvalidLinkState = ({ message }) => (
  <>
    <XCircle size={48} className="text-red-400 mx-auto mb-4" />
    <p data-testid="reschedule-error-message" className="text-white/70">{message}</p>
  </>
);

const SuccessState = ({ t }) => (
  <>
    <CheckCircle2 size={48} className="text-gold mx-auto mb-4" />
    <h1 className="font-serif text-2xl text-white mb-2">{t("reschedulePage.successTitle")}</h1>
    <p className="text-white/60">{t("reschedulePage.successMessage")}</p>
  </>
);

const RescheduleForm = ({ t, lang, booking, token, date, time, error, submitting, setDate, setTime, onConfirm }) => (
  <div className="text-left">
    <h1 className="font-serif text-2xl text-white mb-4 text-center">{t("reschedulePage.title")}</h1>
    <p className="text-white/40 text-xs uppercase tracking-widest mb-2">{t("reschedulePage.currentAppointment")}</p>
    <div className="bg-[#1A1A1A] rounded-lg p-4 mb-6 space-y-1.5 text-sm">
      {booking.appointment_number && (
        <p className="flex justify-between text-white/70"><span>{t("manageAppointmentPage.appointmentNumber")}</span><span className="text-gold font-semibold">#{booking.appointment_number}</span></p>
      )}
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.service")}</span><span className="text-white">{lang === "nl" ? booking.service_name_nl : booking.service_name_en}</span></p>
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.date")}</span><span className="text-white">{booking.date}</span></p>
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.time")}</span><span className="text-white">{booking.start_time}</span></p>
    </div>

    <p className="text-white/40 text-xs uppercase tracking-widest mb-3">{t("reschedulePage.newTime")}</p>
    <DateStep selectedDate={date} onSelect={(d) => { setDate(d); setTime(null); }} />
    {date && (
      <div className="mt-6">
        <TimeStep serviceId={booking.service_id} date={date} selectedTime={time} onSelect={setTime} />
      </div>
    )}

    <p data-testid="reschedule-policy-note" className="text-gold/70 text-xs bg-gold/5 border border-gold/20 rounded-md p-3 mt-6 leading-relaxed">{t("reschedulePage.policyNote")}</p>
    {error && <p data-testid="reschedule-error-message" className="text-red-400 text-sm mt-4">{error}</p>}

    <Button data-testid="reschedule-confirm-button" onClick={onConfirm} disabled={!date || !time || submitting} className="w-full mt-6 bg-gold text-white hover:bg-gold-hover rounded-none disabled:opacity-50">
      {submitting ? t("reschedulePage.confirming") : t("reschedulePage.confirmButton")}
    </Button>
    <Link to={`/afspraak-annuleren?token=${token}`} data-testid="reschedule-page-cancel-link" className="block text-white/50 text-xs mt-4 text-center hover:text-gold underline underline-offset-4">
      {t("reschedulePage.cancelLink")}
    </Link>
  </div>
);

export default function RescheduleAppointment() {
  const { t, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState(null);

  useEffect(() => {
    if (!token) {
      setError(t("cancelPage.invalidLink"));
      return;
    }
    api.get(`/bookings/by-token/${token}`).then(({ data }) => setBooking(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [token, t]);

  const handleConfirm = async () => {
    if (!date || !time) return;
    setSubmitting(true);
    setError("");
    const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, "0"), d = String(date.getDate()).padStart(2, "0");
    try {
      await api.put(`/bookings/manage/${token}`, { date: `${y}-${m}-${d}`, start_time: time });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reschedule-appointment-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-lg w-full bg-[#141414] border border-white/10 rounded-xl p-8 text-center">
        {error && !booking && <InvalidLinkState message={error} />}
        {booking && booking.status === "cancelled" && <InvalidLinkState message={t("cancelPage.invalidLink")} />}
        {booking && done && <SuccessState t={t} />}
        {booking && booking.status !== "cancelled" && !done && (
          <RescheduleForm t={t} lang={lang} booking={booking} token={token} date={date} time={time} error={error} submitting={submitting} setDate={setDate} setTime={setTime} onConfirm={handleConfirm} />
        )}
      </div>
    </div>
  );
}
